"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Check, X } from "lucide-react";

interface ComparisonRow {
  feature: string;
  a: boolean;
  b: boolean;
}

interface ChecklistComparisonProps {
  title?: string;
  subtitle?: string;
  columnA: string;
  columnB: string;
  rows?: ComparisonRow[];
  comparisonRows?: ComparisonRow[];
}

const ChecklistComparison = ({ title, subtitle, columnA, columnB, rows, comparisonRows }: ChecklistComparisonProps) => {
  const items = comparisonRows || rows;
  if (!items || items.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-12 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Comparison Table */}
        <BlurFade delay={0.2}>
          <div className="rounded-3xl border border-foreground/5 overflow-hidden bg-muted/20">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_120px_120px] md:grid-cols-[1fr_160px_160px] bg-muted/50 border-b border-foreground/10">
              <div className="py-4 px-6 text-sm font-bold text-foreground uppercase tracking-wider">
                Feature
              </div>
              <div className="py-4 px-4 text-sm font-bold text-foreground uppercase tracking-wider text-center">
                {columnA}
              </div>
              <div className="py-4 px-4 text-sm font-bold text-foreground uppercase tracking-wider text-center">
                {columnB}
              </div>
            </div>

            {/* Rows */}
            {items.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-[1fr_120px_120px] md:grid-cols-[1fr_160px_160px] border-b border-foreground/5 last:border-b-0 ${
                  i % 2 === 0 ? "bg-white" : "bg-muted/20"
                } hover:bg-muted/40 transition-colors`}
              >
                <div className="py-4 px-6 text-sm font-medium text-foreground">
                  {row.feature}
                </div>
                <div className="py-4 px-4 flex items-center justify-center">
                  {row.a ? (
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={16} className="text-green-600" strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                      <X size={16} className="text-red-400" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
                <div className="py-4 px-4 flex items-center justify-center">
                  {row.b ? (
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={16} className="text-green-600" strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                      <X size={16} className="text-red-400" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default ChecklistComparison;
